import type { ISelectProps } from '@oliasoft-open-source/react-ui-library';
import httpClient from '~utils/httpClient';
import type { ICustomerUpdateExtension } from '../../services/DTO.service';
import { API_ENDPOINTS, DELETE_TYPES } from './constants';
import type {
  ICustomerApplication,
  ICustomerCreationData,
  ICustomerCreationPayload,
  ICustomerCreationResponse,
  ICustomerDeleteTarget,
  ICustomerDetails,
  ICustomerForEdit,
  ICustomerFullResponse,
  ICustomerLicensesResponse,
  ICustomerShort,
  ICustomerUser,
  ICustomerUsersWithRolesResponse,
  ICustomerWithUsers,
  IMessageResponse,
  IOrganizationCustomer,
  TAccountRepresentative,
  TCustomerId,
} from './interfaces';

export type TCustomerUpdateData = Partial<ICustomerDetails> & ICustomerUpdateExtension;

export type TCustomerUserData = Partial<ICustomerUser>;

export function createCustomer(data: ICustomerCreationPayload) {
  return httpClient.post<ICustomerCreationResponse>(API_ENDPOINTS.customer, data);
}
export const getOrganizationsWithCustomers = async () => {
  const data = await httpClient.get<IOrganizationCustomer[]>(API_ENDPOINTS.getOrganizationsWithCustomers);
  return data;
};

export const getCustomers = () => httpClient.get<ICustomerShort[]>(API_ENDPOINTS.customersShortInfo);

export const getCustomer = (id: TCustomerId) => httpClient.get<ICustomerFullResponse>(`${API_ENDPOINTS.customer}/${id}`);
export const getCustomerShort = (id: TCustomerId) => httpClient.get<ICustomerShort>(`${API_ENDPOINTS.customerShort}/${id}`);

export const getCurrentCustomer = () => httpClient.get<ICustomerFullResponse>(API_ENDPOINTS.currentCustomer);
export const getCurrentCustomerShort = () => httpClient.get<ICustomerShort>(API_ENDPOINTS.currentCustomerShort);

export const getCurrentCustomerApplications = () => httpClient.get<ICustomerApplication[]>(API_ENDPOINTS.currentCustomerApplications);
export const getCustomerApplications = (id: TCustomerId) => httpClient.get<ICustomerApplication[]>(`${API_ENDPOINTS.customerApplications}/${id}`);

export const getCustomerCreationData = () => httpClient.get<ICustomerCreationData>(API_ENDPOINTS.getCustomerCreationData);

export const updateCustomer = (data: TCustomerUpdateData, customerId: TCustomerId) => customerId ? httpClient.patch<IMessageResponse>(`${API_ENDPOINTS.customer}/${customerId}`, data) : httpClient.patch<IMessageResponse>(`${API_ENDPOINTS.currentCustomer}`, data);

export const getExternalIdPs = () => httpClient.get<ISelectProps['options']>(API_ENDPOINTS.getExternalIdPs);

export const fetchCustomerForEdit = async (id?: TCustomerId): Promise<{ data: ICustomerForEdit }> => {
  const getCustomerToEdit = id ? getCustomer(id) : getCurrentCustomer();
  const [customerResult, creationDataResult, externalIdPsResult] = await Promise.allSettled([
    getCustomerToEdit,
    getCustomerCreationData(),
    getExternalIdPs(),
  ]);
  if (customerResult.status === 'rejected') {
    throw customerResult.reason;
  }
  if (creationDataResult.status === 'rejected') {
    throw creationDataResult.reason;
  }
  const { data: customer } = customerResult.value;

  return {
    data: {
      customer: { ...customer, externalIdentifications: customer.externalIdentifications?.externalIdentificationNames },
      types: creationDataResult.value.data.types,
      externalIdPs: externalIdPsResult.status === 'fulfilled' ? externalIdPsResult.value.data : undefined,
    },
  };
};

export const changeAccountManager = (customerId: TCustomerId, subjectId: string) => customerId
  ? httpClient.patch(`${API_ENDPOINTS.customerAccountManager}/${customerId}`, { subjectId })
  : httpClient.patch(`${API_ENDPOINTS.currentCustomerAccountManager}`, { subjectId });

export const changeAccountRepresentative = (customerId: TCustomerId, subjectId: string) => httpClient.patch(`${API_ENDPOINTS.customerAccountRepresentative}/${customerId}`, { subjectId });

export const removeAccountManager = (customerId: TCustomerId) => {
  return customerId ? httpClient.delete(`${API_ENDPOINTS.customerAccountManager}/${customerId}`) : httpClient.delete(`${API_ENDPOINTS.currentCustomerAccountManager}`);
};

export const removeAccountRepresentative = (customerId: TCustomerId) => httpClient.delete(`${API_ENDPOINTS.customerAccountRepresentative}/${customerId}`);

export const addUserToCustomer = (user: TCustomerUserData, customerId: TCustomerId) => customerId ? httpClient.post<IMessageResponse>(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`, user) : httpClient.post<IMessageResponse>(`${API_ENDPOINTS.getCurrentCustomerUsers}`, user);

export const getCustomerUsers = (customerId?: TCustomerId | null) => customerId ? httpClient.get<ICustomerWithUsers>(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`) : httpClient.get<ICustomerWithUsers>(`${API_ENDPOINTS.getCurrentCustomerUsers}`);

export const getCustomerUsersWithRole = (customerId?: TCustomerId) => customerId ? httpClient.get<ICustomerUsersWithRolesResponse>(`${API_ENDPOINTS.getCustomerUsersWithRoles}/${customerId}`) : httpClient.get<ICustomerUsersWithRolesResponse>(`${API_ENDPOINTS.getCurrentCustomerUsersWithRoles}`);

export const fetchCustomerLicenses = (id?: TCustomerId) => (id ? httpClient.get<ICustomerLicensesResponse>(`${API_ENDPOINTS.getCustomerLicenses}/${id}`) : httpClient.get<ICustomerLicensesResponse>(`${API_ENDPOINTS.getCurrentCustomerLicenses}`));

export const removeUser = (userId: number, customerId?: TCustomerId) => customerId ? httpClient.delete(`${API_ENDPOINTS.getCustomerUsers}/${userId}`) : httpClient.delete(`${API_ENDPOINTS.getCurrentCustomerUsers}/${userId}`);

export const editUser = (user: TCustomerUserData, customerId?: TCustomerId) => customerId ? httpClient.patch(`${API_ENDPOINTS.getCustomerUsers}/${customerId}`, user) : httpClient.patch(`${API_ENDPOINTS.getCurrentCustomerUsers}`, user);

export const getCustomerAccountRepresentative = () => httpClient.get<TAccountRepresentative[]>(API_ENDPOINTS.getCustomerAccountRepresentative);
export const checkUserAssignedToCustomer = () => httpClient.get<{ hasCustomer: boolean }>(API_ENDPOINTS.checkUserAssignedToCustomer);

export const addExternalUserToCustomer = (userIds: number[], customerId?: TCustomerId) => customerId
  ? httpClient.post(`${API_ENDPOINTS.getCustomerUsersExternal}/${customerId}`, { userIds })
  : httpClient.post(API_ENDPOINTS.getCurrentCustomerUsersExternal, { userIds });

export const removeExternalUserFromCustomer = (subjectId: string, customerId?: TCustomerId) => customerId
  ? httpClient.delete(`${API_ENDPOINTS.getCustomerUsersExternal}/${customerId}?subjectId=${subjectId}`)
  : httpClient.delete(`${API_ENDPOINTS.getCurrentCustomerUsersExternal}?subjectId=${subjectId}`);

export const deleteCustomer = (customer: Pick<ICustomerDeleteTarget, 'customerId' | 'deletedAt'>) => {
  const { customerId, deletedAt } = customer;
  const deleteType = deletedAt ? DELETE_TYPES.HARD : DELETE_TYPES.SOFT;
  return httpClient.delete<IMessageResponse>(`${API_ENDPOINTS.customers}/${customerId}?deleteType=${deleteType}`);
};

export const restoreCustomer = (customerId: TCustomerId) => httpClient.get<IMessageResponse>(`/customer-restore/${customerId}`);

export const changeLocalLoginStrategyBulk = (type: boolean, customerId: TCustomerId) => httpClient.patch(`${API_ENDPOINTS.changeLocalLoginStrategyBulk}/${customerId}?type=${type}`);

export const changeCustomerFeatureAccess = (type: string, customerId: TCustomerId, value: boolean) => httpClient.patch(`${API_ENDPOINTS.changeCustomerFeatureAccess}/${customerId}?type=${type}&value=${value}`);

export const updateMFABulk = (enableMFA: boolean, customerId: TCustomerId) => httpClient.patch(`${API_ENDPOINTS.updateMFABulk}/${customerId}?enableMFA=${enableMFA}`);

export const fetchOliasoftUsers = () => httpClient.get<ICustomerUsersWithRolesResponse>(API_ENDPOINTS.oliasoftUsers);

export const requestExternalUserByEmail = (email: string, customerId: TCustomerId) => httpClient.post(API_ENDPOINTS.requestExternalUser, { email, customerId });

export const customerDataWipe = (customerId: TCustomerId) => httpClient.delete(`${API_ENDPOINTS.dataWipe}/${customerId}`);
